'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.25, 0.4, 0.25, 1] as const },
  }),
};

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-surface-base">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(0,201,160,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,201,160,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="absolute -top-48 -left-48 w-[700px] h-[700px] bg-accent/[0.05] rounded-full blur-[140px]" />
        <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-blue-600/[0.04] rounded-full blur-[120px]" />
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-surface-deepest to-transparent pointer-events-none z-10" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-24">
        <motion.div custom={0} variants={fadeUp} initial="hidden" animate="visible" className="mb-8">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-accent/20 bg-accent/[0.06] text-accent text-xs font-medium tracking-wide uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            From the team behind McAfee ePolicy Orchestrator
          </span>
        </motion.div>

        <motion.h1 custom={1} variants={fadeUp} initial="hidden" animate="visible"
          className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[1.0] tracking-tight mb-6 max-w-4xl">
          <span className="text-white/90">Security that</span>
          <br />
          <span className="text-accent">fixes itself.</span>
        </motion.h1>

        <motion.p custom={2} variants={fadeUp} initial="hidden" animate="visible"
          className="text-lg sm:text-xl text-zinc-400 leading-relaxed max-w-2xl mb-16">
          One platform, two layers. A discovery layer that continuously finds what is exposed, and a remediation layer that fixes it and verifies it in production. We sit on the tools you already run and own the last mile.
        </motion.p>

        {/* Product cards */}
        <motion.div custom={3} variants={fadeUp} initial="hidden" animate="visible"
          className="grid sm:grid-cols-2 gap-4 max-w-3xl mb-16">
          {[
            {
              href: '/autofix', label: 'CyberArmy AutoFix', tag: 'Enterprise', tagColor: 'emerald',
              headline: 'Autonomous Remediation',
              desc: 'Sits on the tools you already run and owns the last mile: fixes merged, deployed, and verified in production. Never Break Prod™.',
              cta: 'Request Demo', ctaHref: '/contact?lead_type=autofix_demo&source=home_hero',
            },
            {
              href: '/cyberswarm', label: 'Cyber Swarm', tag: 'Early Access', tagColor: 'blue',
              headline: 'AI Penetration Testing',
              desc: 'Full external pentest in 20 minutes. Active exploitation, zero false positives. SOC 2 / ISO 27001 accepted.',
              cta: 'Get a Free AI Pentest Report', ctaHref: '/free-pentest?source=home_hero',
            },
          ].map((p) => (
            <div key={p.href} className={`rounded-2xl border bg-surface-1 p-7 flex flex-col gap-5 ${p.tagColor === 'emerald' ? 'border-accent/20' : 'border-accent-secondary/15'}`}>
              <div className="flex items-start justify-between gap-3">
                <span className="text-sm font-semibold text-white/90">{p.label}</span>
                <span className={`text-[10px] font-medium px-2.5 py-1 rounded-full border flex-shrink-0 ${p.tagColor === 'emerald' ? 'bg-accent/8 border-accent/20 text-accent' : 'bg-accent-secondary/8 border-accent-secondary/15 text-accent-secondary'}`}>{p.tag}</span>
              </div>
              <div>
                <p className={`text-xs font-medium mb-2 ${p.tagColor === 'emerald' ? 'text-accent' : 'text-accent-secondary'}`}>{p.headline}</p>
                <p className="text-sm text-zinc-500 leading-relaxed">{p.desc}</p>
              </div>
              <Link href={p.ctaHref} className={`group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 w-fit ${p.tagColor === 'emerald' ? 'bg-accent text-black hover:bg-accent-hover shadow-lg shadow-accent/20' : 'border border-accent-secondary/25 text-accent-secondary hover:border-accent-secondary/40 hover:text-accent-secondary-light hover:bg-accent-secondary/[0.06]'}`}>
                {p.cta}
                <svg className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
              </Link>
            </div>
          ))}
        </motion.div>

        {/* Stats */}
        <motion.div custom={4} variants={fadeUp} initial="hidden" animate="visible" className="flex flex-wrap gap-8 items-center">
          {[
            { val: '<5 min', label: 'mean time to fix', color: 'text-accent' },
            { val: '<60s', label: 'automated rollback', color: 'text-accent' },
            { val: '20 min', label: 'pentest to report', color: 'text-accent-secondary' },
            { val: '32+ yrs', label: 'endpoint security pedigree', color: 'text-zinc-300' },
          ].map((s) => (
            <div key={s.label} className="flex items-baseline gap-2">
              <span className={`text-2xl font-bold ${s.color}`}>{s.val}</span>
              <span className="text-xs text-zinc-600 uppercase tracking-wider">{s.label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
